import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import InputText from "../../../../components/inputs/InputText";
import InputDropSearch from "../../../../components/inputs/InputDropSearch";
import AddProductModal from "../../purchase/components/AddProductModal";
import {
  createPurchaseOrder,
  findPurchaseOrderById,
  searchPurchaseSuppliers,
  updatePurchaseOrder,
} from "../../../../api/inventoryService";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../../redux/loader/loaderActions";
import { describeOrdered, describeOutstanding } from "../../purchaseOrderUtils";
import {
  isOtherSupplierNameMissing,
  otherSupplierFromRecord,
  otherSupplierPayload,
  otherSupplierPick,
} from "../../otherSupplierUtils";
import OtherSupplierFields from "../../OtherSupplierFields";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

const formatAmount = (value: number | string | undefined) => {
  const num = typeof value === "string" ? parseFloat(value) : value ?? 0;
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
};

interface Supplier {
  [key: string]: any;
}

function AddPurchaseOrder() {
  const { ID } = useParams();
  const isEditMode = ID !== undefined;
  const navigate = useNavigate();

  const [formData, setFormData] = useState<any>({
    supplierId: "",
    supplierIsOther: false,
    otherSupplierName: "",
    otherSupplierPhone: "",
    orderNo: "",
    orderDate: "",
    expectedDate: "",
    note: "",
    products: [],
    status: "open",
    errors: false,
  });
  const [supplier, setSupplier] = useState<Supplier[]>([]);
  const [openAddProduct, setOpenAddProduct] = useState(false);

  const orderTotal = useMemo(
    () =>
      formData.products.reduce(
        (sum: number, line: any) => sum + (Number(line?.price) || 0),
        0
      ),
    [formData.products]
  );

  const getSupplierData = useCallback((search: string) => {
    searchPurchaseSuppliers(search)
      .then((data) => setSupplier(data as Supplier[]))
      .catch((error) => console.error(error?.response));
  }, []);

  useEffect(() => {
    getSupplierData("");
  }, [getSupplierData]);

  useEffect(() => {
    if (!ID) return;
    findPurchaseOrderById(ID)
      .then((data: any) => {
        setFormData({
          supplierId: data?.supplierId?._id ?? data?.supplierId ?? "",
          ...otherSupplierFromRecord(data),
          orderNo: data?.orderNo ?? "",
          orderDate: data?.orderDate ?? "",
          expectedDate: data?.expectedDate ?? "",
          note: data?.note ?? "",
          products: data?.products ?? [],
          status: data?.status ?? "open",
          errors: false,
        });
      })
      .catch((error) => console.error(error?.response));
  }, [ID]);

  const handleChange = useCallback((e: any) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
      ...(name === "supplierId" ? otherSupplierPick(e.target) : {}),
    }));
  }, []);

  const addProduct = useCallback((line: any) => {
    setFormData((prev: any) => ({
      ...prev,
      products: [...prev.products, line],
    }));
    setOpenAddProduct(false);
  }, []);

  const removeProduct = useCallback((index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      products: prev.products.filter((_: any, i: number) => i !== index),
    }));
  }, []);

  // Received lines are already billed, so the order can no longer be edited
  // out from under them.
  const locked = isEditMode && formData.status !== "open";

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (
      !formData.supplierId ||
      !formData.orderNo ||
      !formData.orderDate ||
      isOtherSupplierNameMissing(formData)
    ) {
      setFormData((prev: any) => ({ ...prev, errors: true }));
      return;
    }

    setloadeActive();
    const payload = {
      supplierId: formData.supplierId,
      ...otherSupplierPayload(formData),
      orderNo: formData.orderNo,
      orderDate: formData.orderDate,
      expectedDate: formData.expectedDate || null,
      note: formData.note || null,
      products: formData.products,
    };
    const request = ID
      ? updatePurchaseOrder(ID, payload)
      : createPurchaseOrder(payload);

    request
      .then(() => {
        setloadeDeactive();
        navigate("/client/inventory/purchase-order/all-order");
      })
      .catch((error) => {
        setloadeDeactive();
        console.error(error?.response);
        toast.error(
          error?.response?.data?.message || "Could not save the order"
        );
      });
  };

  return (
    <>
      {openAddProduct && (
        <AddProductModal
          closeModal={() => setOpenAddProduct(false)}
          addProduct={addProduct}
        />
      )}

      <div className="space-y-3">
        <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3">
          <div className="flex items-start gap-2.5">
            <button
              type="button"
              onClick={() => navigate("/client/inventory/purchase-order/all-order")}
              className="mt-0.5 w-7 h-7 rounded-md bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors shrink-0"
              title="Back to orders"
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-violet-100 dark:bg-violet-900/40 rounded-md flex items-center justify-center">
                  <ReceiptLongIcon className="text-violet-600 dark:text-violet-300" sx={{ fontSize: 14 }} />
                </div>
                <h1 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                  {isEditMode ? "Edit Purchase Order" : "New Purchase Order"}
                </h1>
              </div>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 ml-9">
                What you are asking the supplier for. Invoices are billed
                against it later.
              </p>
            </div>
          </div>
        </div>

        {locked && (
          <div className="px-3 py-2 rounded-[8px] bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-700 dark:text-amber-300">
            This order is {formData.status}. Reopen it from the order list to
            make changes.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3">
            <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-3 uppercase tracking-wide">
              Order Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <InputDropSearch
                data={supplier}
                name="supplierId"
                required
                label="Supplier Name"
                selectName1="supplierName"
                selectName2="suppliercompany"
                selectValue="_id"
                value={formData.supplierId}
                onChange={handleChange}
                onSearch={(e: string) => getSupplierData(e)}
                // Invoices already linked were matched against this supplier.
                isDisable={isEditMode}
                errors={formData.errors && formData.supplierId === ""}
              />
              <InputText
                name="orderNo"
                required
                label="Order Number"
                value={formData.orderNo}
                onChange={handleChange}
                isDisable={locked}
                errors={formData.errors && formData.orderNo === ""}
              />
              <InputText
                name="orderDate"
                required
                label="Order Date"
                value={formData.orderDate}
                onChange={handleChange}
                type="date"
                isDisable={locked}
                errors={formData.errors && formData.orderDate === ""}
              />
              <InputText
                name="expectedDate"
                label="Expected Delivery"
                value={formData.expectedDate}
                onChange={handleChange}
                type="date"
                isDisable={locked}
              />
            </div>
            {formData.supplierIsOther && (
              <OtherSupplierFields
                name={formData.otherSupplierName}
                phone={formData.otherSupplierPhone}
                onChange={handleChange}
                // Fixed with the supplier: invoices are matched on it.
                isDisable={isEditMode}
                errors={formData.errors && isOtherSupplierNameMissing(formData)}
              />
            )}
            <InputText
              name="note"
              label="Note"
              value={formData.note}
              onChange={handleChange}
              isDisable={locked}
              placeholder="Anything the supplier should know"
            />
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-600 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <InventoryIcon className="text-indigo-500 dark:text-indigo-300" sx={{ fontSize: 16 }} />
                <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                  Ordered Items
                </h2>
              </div>
              {!locked && (
                <button
                  type="button"
                  onClick={() => setOpenAddProduct(true)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all font-medium text-xs"
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                  Add Item
                </button>
              )}
            </div>

            {formData.products.length === 0 ? (
              <div className="text-center py-8 text-slate-400 dark:text-slate-500">
                <InventoryIcon sx={{ fontSize: 32 }} className="mb-2" />
                <p className="text-xs">Nothing ordered yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-600">
                      {["No", "Item", "Ordered", "Outstanding", "Expected", ""].map(
                        (title, i) => (
                          <th
                            key={i}
                            className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider"
                          >
                            {title}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                    {formData.products.map((item: any, i: number) => (
                      <tr key={i} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 text-[10px] font-semibold rounded-md bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300">
                            {i + 1}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs font-medium text-slate-800 dark:text-slate-100">
                          {item?.productName}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                          <span>{describeOrdered(item)}</span>
                          {item?.packName && (
                            <span className="block text-[10px] text-slate-400 dark:text-slate-500">
                              = {item?.quantity} {item?.scale}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {item?.outstanding === undefined ? (
                            <span className="text-slate-400 dark:text-slate-500">—</span>
                          ) : item.outstanding === 0 ? (
                            <span className="text-emerald-600 dark:text-emerald-300 font-medium">
                              Received
                            </span>
                          ) : (
                            <span className="text-amber-600 dark:text-amber-300">
                              {describeOutstanding(item)}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
                          {formatAmount(item?.price)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {!locked && (
                            <button
                              type="button"
                              onClick={() => removeProduct(i)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-red-200 dark:border-red-800 text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors text-xs font-medium"
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-600 flex items-center justify-between">
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Expected order total
              </span>
              <span className="text-sm font-bold text-indigo-600 dark:text-indigo-300">
                {formatAmount(orderTotal)}
              </span>
            </div>
          </div>

          {!locked && (
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium hover:shadow-lg hover:shadow-primary/20 transition-all text-xs"
              >
                {isEditMode ? "Update Order" : "Create Order"}
              </button>
            </div>
          )}
        </form>
      </div>
    </>
  );
}

export default AddPurchaseOrder;
